import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from '../assets/CAROLINA.png';
import logo_data from '../assets/logo_data_mng.png';
import { BsFiletypeDoc, BsFileZip, BsCloudMinus, BsCloudUpload, BsFillMoonFill, BsFillSunFill, BsArrowDownUp, BsSearch, BsCheckCircle, BsXCircle, BsInfoCircle, BsX, BsGear, BsCaretDownFill, BsCaretUpFill, BsCheck2Square } from "react-icons/bs";
import "./PatientReferralManagement.css";

import { API_URLS } from '../config/api';
import { Amplify } from 'aws-amplify';
import { getCurrentUser, signOut, fetchAuthSession } from 'aws-amplify/auth';
import outputs from '../amplify_outputs.json';
import '@aws-amplify/ui-react/styles.css';
Amplify.configure(outputs);


function Management() {
    const navigate = useNavigate();
    const [userAuthStatus, setUserAuthStatus] = React.useState('');
    const [alertTimer, setAlertTimer] = useState(false);
    const [ip, setIP] = React.useState('');
    const [dataload, setDataload] = React.useState([]);
    const [droppedFiles, setDroppedFiles] = React.useState('');
    const [progress, setProgress] = React.useState(0);
    const [recordId, setRecordID] = React.useState('');
    const [showUpload, setShowUpload] = React.useState(false);
    const [focusColor, setFocusColor] = React.useState(false);
    const [account, setAccount] = React.useState(true);
    const [colorMode, setColorMode] = React.useState(false);
    const [sortDateToggle, setSortDateToggle] = React.useState(false);
    const [sortPatientNameToggle, setSortPatientNameToggle] = React.useState(false);
    const [sortPractitionerToggle, setSortPractitionerToggle] = React.useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const loadingIntervalRef = useRef(null);
    const [isUploading, setIsUploading] = useState(false);
    const [downloadingId, setDownloadingId] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const [expandedRow, setExpandedRow] = useState(null);
    const [isReviewedMinimized, setIsReviewedMinimized] = useState(false);
    const [transitioningId, setTransitioningId] = useState(null);
    const [generatedDropdownOpen, setGeneratedDropdownOpen] = useState(null);
    const [completeDropdownOpen, setCompleteDropdownOpen] = useState(null);
    // Per-item transient form state for the Complete dropdown: { [id]: { price, complete } }.
    const [completeForm, setCompleteForm] = useState({});
    const [kpiCollapsed, setKpiCollapsed] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    // Tracks which record is mid-flight while a Stripe Checkout session is created.
    const [stripeProcessingId, setStripeProcessingId] = useState(null);
    // Synchronous re-entrancy guard: state updates are async, so a fast double-click
    // could fire two checkout requests before `stripeProcessingId` re-renders. This
    // ref flips immediately and blocks the second call outright.
    const stripeInFlightRef = useRef(false);

    // Toast notification helper
    const showToast = useCallback((message, type = 'info') => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: '', type: '' });
        }, 4000);
    }, []);

    const getAuthHeaders = async () => {
        try {
            const session = await fetchAuthSession();
            if (!session.tokens?.idToken) {
                navigate("/sign-in");
                throw new Error("Session expired");
            }
            const token = session.tokens.idToken.toString();
            return { Authorization: token, "Content-Type": "application/json" };
        } catch (err) {
            if (err.message === "Session expired") throw err;
            navigate("/sign-in");
            throw new Error("Authentication failed");
        }
    };

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            // Search term is already being used in filteredData
        }, 300);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    useEffect(() => {
        const storedDarkMode = localStorage.getItem('colorMode');
        if (storedDarkMode) {
            setColorMode(storedDarkMode === 'true');
        }
    }, []);
    
    const changeFavicon = () => {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = logo_data;
    };

    const findUserAuthenticationStatus = async () => {
        try {
            const session = await fetchAuthSession();
            if (!session.tokens?.idToken) {
                navigate("/sign-in");
                return;
            }
            setUserAuthStatus(await getCurrentUser());
            pageLoad();
        } catch {
            navigate("/sign-in");
        }
    }

    async function signOutHandle() {
        try {
            sessionStorage.removeItem('customTemplate');
            localStorage.removeItem('colorMode');
            await signOut();
            navigate("/sign-in");
        } catch {
            showToast("Sign out failed. Please try again.", "error");
        }
    }

    useEffect(() => {
        changeFavicon();
        findUserAuthenticationStatus();

        axios.post("https://ipinfo.io", null, { timeout: 5000 })
            .then((response) => {
                setIP(response.data.ip);
            })
            .catch(() => { setIP('unavailable'); });

        // Lock the document to an app-shell (internal scroll) only on larger
        // screens. On phones the page scrolls naturally so the stacked lists
        // aren't squeezed into tiny nested scroll panes.
        const lockBody = () => {
            document.body.style.overflow = window.innerWidth > 767 ? "hidden" : "";
        };
        lockBody();
        window.addEventListener("resize", lockBody);
        return () => {
            window.removeEventListener("resize", lockBody);
            document.body.style.overflow = "scroll";
        };
    }, [alertTimer]);

    const handleAccount = () => {
        setAccount(!account);
    };

    const onChangeColorMode = () => {
        setColorMode(!colorMode);
        localStorage.setItem("colorMode", !colorMode);
    }
        
    const pageLoad = useCallback((silent = false) => {
        return new Promise((resolve) => {
            if (!silent) {
                setIsLoading(true);
                setLoadingProgress(0);

                if (loadingIntervalRef.current) clearInterval(loadingIntervalRef.current);

                let current = 0;
                loadingIntervalRef.current = setInterval(() => {
                    current += Math.random() * 10 + 3;
                    if (current >= 90) {
                        current = 90;
                        clearInterval(loadingIntervalRef.current);
                    }
                    setLoadingProgress(Math.round(current));
                }, 80);
            }

            getAuthHeaders().then(headers => axios.post(
                API_URLS.DATA,
                {},
                { headers }
            )).then((response) => {
                if (!silent) {
                    clearInterval(loadingIntervalRef.current);
                    setLoadingProgress(100);
                }
                setTimeout(() => {
                    setDataload(response.data);
                    if (!silent) setIsLoading(false);
                    resolve();
                }, silent ? 0 : 150);
            }).catch(() => {
                if (!silent) {
                    clearInterval(loadingIntervalRef.current);
                    setIsLoading(false);
                }
                showToast("Failed to load patient data. Please refresh the page.", "error");
                resolve();
            });
        });
    }, [showToast]);

    // Finalize (or revert) a referral's completion state. `complete` is the desired
    // target state, `price` an optional amount captured in the dropdown. The backend
    // still uses the "reviewed" operation (unchanged for now); price is passed along
    // so it's already in the payload once the backend is wired to persist it.
    const handleCompleteSubmit = async (id, patient_name, complete, price) => {
        const headers = await getAuthHeaders();
        await axios.post(API_URLS.DATA,
            {
                body: {
                    fileID: id,
                    fileOperation: "reviewed",
                    reviewed: complete,
                    patientName: patient_name,
                    ...(complete ? { sendEmail: true } : {}),
                    ...(price ? { price } : {})
                }
            },
            { headers }
        ).then((response) => {
            showToast(complete ? "Marked as complete" : "Marked as incomplete", "success");
            setCompleteDropdownOpen(null);
            setTransitioningId(id);
            setTimeout(() => {
                setDataload(prev => prev.map(item => item.id === id ? { ...item, reviewed: complete, ...(price ? { price } : {}) } : item));
                setTransitioningId(null);
            }, 400);
        }).catch((response) => {
            showToast("Failed to update completion status", "error");
        });
    }

    // The three billing modes an admin can assign to a record from the Complete
    // dropdown. Mutually exclusive; the chosen value is stored on `billing_mode`.
    const BILLING_MODES = [
        { label: 'Pay Wall', value: 'Pay Wall' },
        { label: 'No Pay Wall', value: 'No Pay Wall' },
        { label: 'Invoice (EoM)', value: 'Invoice (End of Month)' },
    ];

    // Set (or clear) a referral's billing mode. Each billing button applies its
    // value immediately via the same DATA endpoint, using a dedicated "billing"
    // operation so the backend can persist it independently of completion state.
    const handleBillingSubmit = async (id, patient_name, billing_mode) => {
        const headers = await getAuthHeaders();
        await axios.post(API_URLS.DATA,
            {
                body: {
                    fileID: id,
                    fileOperation: "billing",
                    billingMode: billing_mode,
                    patientName: patient_name,
                }
            },
            { headers }
        ).then(() => {
            showToast(`Billing set to ${billing_mode}`, "success");
            setDataload(prev => prev.map(item => item.id === id ? { ...item, billing_mode } : item));
        }).catch(() => {
            showToast("Failed to update billing", "error");
        });
    }

    // ---------------------------------------------------------------------------
    // Stripe integration (TEMPLATE)
    // ---------------------------------------------------------------------------
    // Scaffolding for charging a referral through Stripe Checkout. Nothing here is
    // production-wired yet — the two placeholders below and the backend endpoint
    // must be filled in before this goes live:
    //
    //   1. STRIPE_CONFIG.publishableKey — the pk_live_/pk_test_ key from the Stripe
    //      dashboard. Prefer sourcing it from an env var (REACT_APP_STRIPE_PK) so it
    //      is not hard-committed. It is only needed if you later switch to the
    //      stripe-js redirectToCheckout flow; the URL-redirect flow below does not
    //      need it client-side.
    //   2. STRIPE_CONFIG.checkoutEndpoint — an authenticated Lambda/API Gateway route
    //      that creates a Checkout Session server-side (using the SECRET key, never
    //      the publishable one) and returns { url } to redirect the browser to.
    //
    // Backend (create-checkout-session) is expected to:
    //   - verify the caller's Cognito token (same idToken this file already sends),
    //   - look up the referral by fileID, compute/confirm the amount,
    //   - stripe.checkout.sessions.create({ mode: 'payment', line_items, success_url,
    //     cancel_url, metadata: { fileID, patientName } }),
    //   - return { url: session.url }.
    // A Stripe webhook (checkout.session.completed) should then flip the record's
    // payment status — do NOT trust the client redirect as proof of payment.
    const STRIPE_CONFIG = {
        // TODO: replace with your real publishable key (or read from env).
        publishableKey: process.env.REACT_APP_STRIPE_PK || 'pk_test_REPLACE_ME',
        // TODO: point this at the deployed create-checkout-session endpoint.
        // e.g. `${API_BASE}/stripe/checkout` once added to config/api.js -> API_URLS.
        checkoutEndpoint: process.env.REACT_APP_STRIPE_CHECKOUT_URL || '',
        currency: 'usd',
        // Stripe rejects charges under 50¢ (USD); cap the top end so a fat-fingered
        // price can never trigger a runaway charge. Adjust MAX to your real ceiling.
        minCents: 50,
        maxCents: 99999999, // $999,999.99 — Stripe's per-charge maximum
        // How long to wait on the session-create call before giving up.
        timeoutMs: 20000,
        // Where Stripe returns the user after success/cancel.
        successUrl: `${window.location.origin}/patient-referral-management?payment=success`,
        cancelUrl: `${window.location.origin}/patient-referral-management?payment=cancelled`,
    };

    // True only once a real, absolute HTTPS endpoint has been wired up (not the empty
    // default, not a REPLACE_ME placeholder). Used to disable the button and to fail
    // loudly instead of POSTing a charge to a bogus URL while endpoints don't exist.
    const isStripeConfigured = () => {
        const ep = STRIPE_CONFIG.checkoutEndpoint;
        return typeof ep === 'string'
            && /^https:\/\//i.test(ep)
            && !/REPLACE_ME/i.test(ep);
    };

    // Convert a user-entered dollar string to an integer number of cents WITHOUT
    // floating-point error (0.1 + 0.2 !== 0.3). Returns null for anything that isn't
    // a clean, positive amount with at most two decimal places.
    const dollarsToCents = (raw) => {
        if (raw == null) return null;
        const s = String(raw).trim();
        if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
        const [whole, frac = ''] = s.split('.');
        const cents = Number(whole) * 100 + Number((frac + '00').slice(0, 2));
        return Number.isSafeInteger(cents) ? cents : null;
    };

    // Never hand window.location a URL we haven't verified is a genuine Stripe host.
    // If the backend is ever misconfigured or tampered with, this stops the browser
    // from being redirected to an attacker-controlled page (open-redirect guard).
    const isTrustedStripeUrl = (raw) => {
        try {
            const u = new URL(raw);
            return u.protocol === 'https:'
                && (u.hostname === 'checkout.stripe.com' || u.hostname.endsWith('.stripe.com'));
        } catch {
            return false;
        }
    };

    // A stable idempotency key per attempt so a network retry (or the backend seeing
    // the same request twice) creates at most one Checkout Session / charge.
    const newIdempotencyKey = () => {
        if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
        return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    };

    // Kick off a Stripe Checkout for a single referral. Validates the amount and
    // config locally, asks the backend to create a Checkout Session for `price`
    // (dollars), then redirects the browser to the verified hosted Stripe page.
    const handleStripeCheckout = async (item, price) => {
        // Re-entrancy guard — blocks a second click before React re-renders.
        if (stripeInFlightRef.current) return;

        if (!isStripeConfigured()) {
            showToast("Stripe isn't configured yet — set the checkout endpoint", "error");
            return;
        }

        const amountCents = dollarsToCents(price);
        if (amountCents === null) {
            showToast("Enter a valid price (e.g. 49.99) before charging", "error");
            return;
        }
        if (amountCents < STRIPE_CONFIG.minCents) {
            showToast(`Minimum charge is $${(STRIPE_CONFIG.minCents / 100).toFixed(2)}`, "error");
            return;
        }
        if (amountCents > STRIPE_CONFIG.maxCents) {
            showToast("That amount exceeds the maximum allowed charge", "error");
            return;
        }

        stripeInFlightRef.current = true;
        setStripeProcessingId(item.id);
        try {
            const headers = await getAuthHeaders();
            const response = await axios.post(
                STRIPE_CONFIG.checkoutEndpoint,
                {
                    body: {
                        fileID: item.id,
                        patientName: item.patient_name,
                        amount: amountCents, // smallest currency unit (cents)
                        currency: STRIPE_CONFIG.currency,
                        idempotencyKey: newIdempotencyKey(),
                        successUrl: STRIPE_CONFIG.successUrl,
                        cancelUrl: STRIPE_CONFIG.cancelUrl,
                    }
                },
                { headers, timeout: STRIPE_CONFIG.timeoutMs }
            );

            // Backend returns the hosted Checkout URL to send the browser to.
            const checkoutUrl = response.data?.url || response.data?.body?.url;
            if (!checkoutUrl) {
                showToast("Stripe session created but no redirect URL returned", "error");
                return;
            }
            if (!isTrustedStripeUrl(checkoutUrl)) {
                showToast("Refused an unexpected redirect target from Stripe", "error");
                return;
            }
            // Leaving the app — nothing after this runs on success.
            window.location.assign(checkoutUrl);
            return;
        } catch (err) {
            const status = err?.response?.status;
            if (status === 401 || status === 403 || err?.message === "Session expired" || err?.message === "Authentication failed") {
                showToast("Session expired — please sign in again", "error");
            } else if (status === 404) {
                showToast("Stripe endpoint not found — check configuration", "error");
            } else if (err?.code === 'ECONNABORTED') {
                showToast("Stripe request timed out — please try again", "error");
            } else {
                showToast("Failed to start Stripe checkout", "error");
            }
        } finally {
            stripeInFlightRef.current = false;
            setStripeProcessingId(null);
        }
    };

    // Shared "Complete" dropdown control (used in both the incomplete and complete
    // columns). Opens to an optional price field, a "report complete" checkbox, and a
    // Submit that applies the change. Submit is disabled until the checkbox state
    // differs from the record's current state, so it always represents a real change.
    const renderCompleteControl = (item) => {
        const defaults = { price: item.price ?? '', complete: !!item.reviewed };
        const cf = completeForm[item.id] || defaults;
        const isOpen = completeDropdownOpen === item.id;
        const patch = (p) => setCompleteForm(prev => ({
            ...prev,
            [item.id]: { ...(prev[item.id] || defaults), ...p }
        }));
        return (
            <div className="action-control-dropdown-wrapper action-control-item">
                <button className="action-control-dropdown-btn" onClick={() => {
                    const opening = !isOpen;
                    if (opening) {
                        setCompleteForm(prev => prev[item.id] ? prev : { ...prev, [item.id]: defaults });
                        setTimeout(() => { const row = document.querySelector(`[data-row-id="${item.id}"]`); if (row) row.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
                    }
                    setCompleteDropdownOpen(opening ? item.id : null);
                }}>
                    {item.reviewed ? <BsCheckCircle size={15} /> : <BsCheck2Square size={15} />}
                    <span>Complete</span>
                </button>
                {isOpen && (
                    <div className="action-control-dropdown complete-dropdown" onClick={(e) => e.stopPropagation()}>
                        <div className="billing-mode-row">
                            {BILLING_MODES.map(mode => (
                                <button
                                    key={mode.value}
                                    type="button"
                                    className={`billing-mode-btn${item.billing_mode === mode.value ? ' active' : ''}`}
                                    onClick={() => handleBillingSubmit(item.id, item.patient_name, mode.value)}
                                >
                                    {mode.label}
                                </button>
                            ))}
                        </div>
                        <div className="complete-field">
                            <label className="complete-field-label">Price (optional)</label>
                            <div className="complete-price-input">
                                <span className="complete-price-prefix">$</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={cf.price}
                                    onChange={(e) => patch({ price: e.target.value.replace(/[^0-9.]/g, '') })}
                                />
                            </div>
                        </div>
                        {/* Stripe charge action — only meaningful when this record is
                            behind a pay wall. Requires a valid price and a configured
                            endpoint; the title explains why it's disabled when it is. */}
                        {item.billing_mode === 'Pay Wall' && (
                            <button
                                type="button"
                                className="stripe-checkout-btn"
                                disabled={
                                    dollarsToCents(cf.price) === null
                                    || !isStripeConfigured()
                                    || stripeProcessingId === item.id
                                }
                                title={
                                    !isStripeConfigured()
                                        ? 'Stripe checkout endpoint is not configured yet'
                                        : dollarsToCents(cf.price) === null
                                            ? 'Enter a valid price first'
                                            : 'Charge this referral via Stripe'
                                }
                                onClick={() => handleStripeCheckout(item, cf.price)}
                            >
                                {stripeProcessingId === item.id ? 'Starting Stripe…' : 'Pay with Stripe'}
                            </button>
                        )}
                        <label className="complete-checkbox-row">
                            <input
                                type="checkbox"
                                checked={cf.complete}
                                onChange={(e) => patch({ complete: e.target.checked })}
                            />
                            <span>Report complete</span>
                        </label>
                        <button
                            className="complete-submit-btn"
                            disabled={cf.complete === !!item.reviewed}
                            onClick={() => handleCompleteSubmit(item.id, item.patient_name, cf.complete, cf.price)}
                        >
                            Submit
                        </button>
                    </div>
                )}
            </div>
        );
    }

    const handleFileSelect = (event) => {
        event.preventDefault();
        const files = event.target.files;
        if (!files || !files.length) return;

        const file = files[0];
        setPendingFile(file);
    };

    const cancelFileSelect = () => {
        setPendingFile(null);
    };

    const submitUpload = async () => {
        if (!pendingFile) return;
        const fileToUpload = pendingFile;

        try {
            setIsUploading(true);
            setDroppedFiles(fileToUpload);
            let percentage = 0;
            const authHeaders = await getAuthHeaders();
            const response = await axios.post(
                API_URLS.DATA,
                {
                    body: {
                        fileID: recordId,
                        fileName: 'finalDoc.pdf',
                        fileType: fileToUpload.type,
                        fileOperation: "getSignedUrl",
                    }
                },
                { headers: authHeaders }
            ).catch(() => {
                setIsUploading(false);
                showToast("Failed to get upload URL", "error");
                throw new Error("Upload failed");
            });

            await axios.put(response.data,
                fileToUpload,
                {
                    onUploadProgress: (progressEvent) => {
                        const percentCompleted = Math.round(
                          (progressEvent.loaded * 100) / progressEvent.total
                        );
                        if (percentCompleted !== percentage) {
                            percentage = percentCompleted;
                            setProgress(percentage);
                        }
                    },
                    headers: {
                        "Content-Type": 'application/pdf',
                    },
                }
            ).then(async (res) => {
                if (res.status === 200) {
                    showToast("PDF uploaded successfully!", "success");
                    await pageLoad(true);
                    cancelFileSelect();
                } else {
                    showToast("Upload failed. Please try again.", "error");
                }
                setIsUploading(false);
            }).catch(() => {
                showToast("Upload failed. Please try again.", "error");
                setIsUploading(false);
            });
        } catch (error) {
            showToast("Upload failed. Please try again.", "error");
            setIsUploading(false);
        }
    };

    const itemDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this record? This action cannot be undone.")) {
            const headers = await getAuthHeaders();
            await axios.post(
                API_URLS.DATA,
                {
                    body: {
                        fileID: id,
                        fileOperation: "delete",
                    }
                },
                { headers }
            ).then((response) => {
                showToast("Record deleted successfully", "success");
                pageLoad();
            }).catch((response) => {
                showToast("Failed to delete record", "error");
            });
        }
    }

    const itemDeletePDF = async (id) => {
        if (window.confirm("Are you sure you want to delete this PDF file?")) {
            const headers = await getAuthHeaders();
            await axios.post(
                API_URLS.DATA,
                {
                    body: {
                        fileID: id,
                        fileOperation: "deletePDF",
                    }
                },
                { headers }
            ).then((response) => {
                showToast("PDF deleted successfully", "success");
                pageLoad();
            }).catch((response) => {
                showToast("Failed to delete PDF", "error");
            });
        }
    }

    const getGeneratedDoc = (item, template) => {
        const docs = Array.isArray(item.generatedDoc) ? item.generatedDoc : [item.generatedDoc];
        return docs.find(d => d?.generatedDoc?.toUpperCase().includes('_' + template.toUpperCase() + '.'));
    };

    const itemDownload = async (id, file, patient_name, type, template, practitioner_name) => {
        if (!file) {
            showToast("That file isn't available to download yet.", "error");
            return;
        }
        // Download filename: "<patient>_<practitioner>_<type>" (whatever is
        // present), spaces collapsed to underscores.
        const baseName = ([patient_name, practitioner_name, type]
            .filter(Boolean)
            .join('_')
            .replace(/\s+/g, '_')
            .replace(/_+/g, '_')) || 'download';
        const ext = file && file.includes('.') ? '.' + file.split('.').pop() : '';
        try {
            setDownloadingId(id);
            showToast("Preparing download...", "info");
            const headers = await getAuthHeaders();
            const response = await axios.post(
            API_URLS.DATA,
            {
                body: {
                    fileID: id,
                    fileName: file,
                    fileOperation: "download",
                    downloadTemplate: template,
                    downloadName: baseName + ext,
                }
            },
            { headers }
            ).catch((response) => {
                showToast("Failed to prepare download", "error");
                throw new Error("Download failed");
            });
            // Robustly pull the presigned URL out of whatever shape the API
            // returns: a raw URL string, a JSON-quoted string, an object with a
            // `body`/`url`, or a stringified object. The body is a plain URL
            // (not JSON), so we must NOT blindly JSON.parse it.
            const extractUrl = (value) => {
                if (value == null) return null;
                if (typeof value === 'object') {
                    return extractUrl(value.body ?? value.url ?? null);
                }
                if (typeof value === 'string') {
                    const s = value.trim();
                    if (/^https?:\/\//i.test(s)) return s;          // already a URL
                    if (s.startsWith('"') || s.startsWith('{')) {   // quoted/JSON wrapper
                        try { return extractUrl(JSON.parse(s)); } catch (_) { return null; }
                    }
                }
                return null;
            };
            const presignedUrl = extractUrl(response.data);
            if (!presignedUrl) {
                console.error('[download] could not extract URL from response', response.data);
                throw new Error("Failed to get a valid download URL.");
            }
            // The presigned URL carries Content-Disposition: attachment, so a
            // same-tab navigation downloads it without leaving the page. We must
            // NOT use target="_blank": this runs after an await, so the popup
            // blocker silently kills a new-tab open (no error, no download).
            console.log('[download] navigating to presigned URL', presignedUrl);
            const link = document.createElement('a');
            link.href = presignedUrl;
            link.setAttribute('download', baseName + ext);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            showToast("Download started", "success");
        } catch (error) {
            showToast("Download failed. Please try again.", "error");
        } finally {
            setDownloadingId(null);
        }
    }

    const toggleDateSort = () => {
        if (sortDateToggle) {
            const sortedData = [].concat(dataload).sort((a, b) => new Date(b.dttm) - new Date(a.dttm));
            setDataload(sortedData);
            setSortDateToggle(!sortDateToggle);
        } else {
            const sortedData = [].concat(dataload).sort((a, b) => new Date(a.dttm) - new Date(b.dttm));
            setDataload(sortedData);
            setSortDateToggle(!sortDateToggle);
        }
    }

    const togglePatientNameSort = () => {
        if (sortPatientNameToggle) {
            const sortedData = [].concat(dataload).sort((a, b) => a.patient_name < b.patient_name ? 1 : -1);
            setDataload(sortedData);
            setSortPatientNameToggle(!sortPatientNameToggle);
        } else {
            const sortedData = [].concat(dataload).sort((a, b) => a.patient_name > b.patient_name ? 1 : -1);
            setDataload(sortedData);
            setSortPatientNameToggle(!sortPatientNameToggle);
        }
    }

    const togglePractitionerSort = () => {
        if (sortPractitionerToggle) {
            const sortedData = [].concat(dataload).sort((a, b) => a.practitioner_name < b.practitioner_name ? 1 : -1);
            setDataload(sortedData);
            setSortPractitionerToggle(!sortPractitionerToggle);
        } else {
            const sortedData = [].concat(dataload).sort((a, b) => a.practitioner_name > b.practitioner_name ? 1 : -1);
            setDataload(sortedData);
            setSortPractitionerToggle(!sortPractitionerToggle);
        }
    }

    const openRowActions = (item) => {
        toggleUpload(item.id);
        setRecordID(item.id);
        setGeneratedDropdownOpen(null);
        setFocusColor(prev => !prev);
    };

    const toggleUpload = async (id) => {
        if (expandedRow === id) {
            setExpandedRow(null);
            setShowUpload(false);
        } else {
            setExpandedRow(id);
            setShowUpload(true);
            setTimeout(() => {
                const row = document.querySelector(`[data-row-id="${id}"]`);
                if (row) row.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
        }
    };
    
    const notReviewedCounter = ((dataload || []).filter(item => item.reviewed === false));
    const reviewedCounter = ((dataload || []).filter(item => item.reviewed === true));
    const buildSearchHaystack = (item) => {
        const parts = [
            item.patient_name, item.practitioner_name, item.office_number,
            item.contact_email, item.payment_method, item.regions_of_interest,
            item.report, item.report_plus,
            item.patient_dob, item.patient_scan_date, item.dttm, item.reviewed_dttm,
        ];
        if (item.report_sections) {
            try {
                const secs = JSON.parse(item.report_sections);
                if (Array.isArray(secs)) {
                    for (const s of secs) parts.push(s.title, s.content);
                } else {
                    parts.push(item.report_sections);
                }
            } catch {
                parts.push(item.report_sections);
            }
        }
        return parts
            .filter(Boolean)
            .join(' ')
            .replace(/<[^>]*>/g, ' ')
            .toLowerCase();
    };

    const filteredData = (dataload || []).filter(item => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return true;
        const haystack = buildSearchHaystack(item);
        return q.split(/\s+/).every(tok => haystack.includes(tok));
    });

    const kpiData = React.useMemo(() => {
        const data = dataload || [];
        const total = data.length;
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const thisMonth = data.filter(item => {
            const d = new Date(item.dttm);
            return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
        });
        const monthlyCount = thisMonth.length;

        const doctorCounts = {};
        data.forEach(item => {
            const name = item.practitioner_name || 'Unknown';
            doctorCounts[name] = (doctorCounts[name] || 0) + 1;
        });
        const topDoctor = Object.entries(doctorCounts).sort((a, b) => b[1] - a[1])[0];

        const lastWeek = data.filter(item => {
            const d = new Date(item.dttm);
            return (now - d) <= 7 * 24 * 60 * 60 * 1000;
        }).length;

        const reviewedPercent = total > 0 ? Math.round((reviewedCounter.length / total) * 100) : 0;

        const officeDomains = new Set();
        let newestDoctor = null;
        let newestDoctorDate = null;
        data.forEach(item => {
            const email = item.contact_email || '';
            const domain = email.split('@')[1];
            if (domain) {
                officeDomains.add(domain.toLowerCase());
            }
            const d = new Date(item.dttm);
            if (item.practitioner_name && (!newestDoctorDate || d > newestDoctorDate)) {
                newestDoctorDate = d;
                newestDoctor = item.practitioner_name;
            }
        });
        const uniqueOffices = officeDomains.size;

        // Avg per month
        const months = new Set();
        data.forEach(item => {
            const d = new Date(item.dttm);
            if (!isNaN(d)) months.add(`${d.getFullYear()}-${d.getMonth()}`);
        });
        const avgPerMonth = months.size > 0 ? (total / months.size).toFixed(1) : '0';

        // Payment method breakdown
        const paymentCounts = {};
        data.forEach(item => {
            const method = item.payment_method || 'Unknown';
            paymentCounts[method] = (paymentCounts[method] || 0) + 1;
        });
        const topPayment = Object.entries(paymentCounts).sort((a, b) => b[1] - a[1])[0];

        // Avg time to review (days between dttm and reviewed_dttm)
        const reviewTimes = [];
        data.forEach(item => {
            if (item.reviewed && item.reviewed_dttm && item.dttm) {
                const submitted = new Date(item.dttm);
                const reviewed = new Date(item.reviewed_dttm);
                const days = (reviewed - submitted) / (1000 * 60 * 60 * 24);
                if (days >= 0) reviewTimes.push(days);
            }
        });
        const avgReviewTime = reviewTimes.length > 0
            ? (reviewTimes.reduce((a, b) => a + b, 0) / reviewTimes.length)
            : null;
        let avgReviewLabel = '—';
        if (avgReviewTime !== null) {
            if (avgReviewTime < 1) avgReviewLabel = `${Math.round(avgReviewTime * 24)}h`;
            else avgReviewLabel = `${avgReviewTime.toFixed(1)}d`;
        }

        return { total, monthlyCount, topDoctor, lastWeek, reviewedPercent, newestDoctor, uniqueOffices, avgPerMonth, topPayment, avgReviewLabel };
    }, [dataload, reviewedCounter.length]);

    return (
        <div id="body" data-theme={colorMode}>
            {isLoading && (
                <div className="loading-overlay">
                    <div className="loading-logo-wrap">
                        <svg className="loading-svg" viewBox="-2 0 204 82" xmlns="http://www.w3.org/2000/svg">
                            <g className="wave-line wave-line-1">
                                <path fill="#1A2A45" d="M 199.78 34.52 L 199.78 42.1 C 199.3 41.98 198.82 41.85 198.35 41.72 C 186.89 38.51 179.0 34.11 170.85 29.57 C 162.19 24.73 153.23 19.73 138.8 15.58 C 115.09 8.74 96.14 14.26 74.58 23.22 C 70.77 24.8 66.88 26.49 62.87 28.24 C 62.57 28.37 62.27 28.5 61.97 28.63 C 54.68 31.81 47.14 35.1 38.93 38.3 C 32.82 40.69 27.04 42.68 21.64 44.34 C 13.52 46.84 6.27 48.58 -0.03 49.81 L -0.03 42.43 C 9.49 40.46 21.23 37.23 34.77 31.95 C 39.71 30.02 44.43 28.05 49.01 26.09 C 51.86 24.86 54.66 23.64 57.43 22.44 C 58.8 21.84 60.15 21.25 61.5 20.67 C 87.91 9.22 111.36 0.08 142.03 8.91 C 157.62 13.41 167.51 18.92 176.26 23.79 C 183.6 27.88 190.38 31.68 199.78 34.52"/>
                            </g>
                            <g className="wave-line wave-line-2">
                                <path fill="#8A9BAE" d="M 199.78 41.76 L 199.78 48.88 C 196.69 48.82 193.65 48.72 190.65 48.58 C 147.44 46.77 113.86 39.31 86.32 33.19 C 77.94 31.33 70.1 29.59 62.87 28.24 C 57.96 27.31 53.34 26.56 49.01 26.09 C 46.16 25.76 43.44 25.54 40.85 25.47 C 26.5 25.06 11.82 28.06 -0.03 31.52 L -0.03 23.88 C 12.1 20.59 26.64 17.94 41.19 18.35 C 47.43 18.53 54.2 19.38 61.5 20.67 C 65.69 21.4 70.04 22.26 74.58 23.22 C 79.15 24.18 83.92 25.24 88.86 26.35 C 117.46 32.7 152.65 40.52 198.35 41.72 C 198.82 41.74 199.31 41.75 199.78 41.76"/>
                            </g>
                            <g className="wave-line wave-line-3">
                                <path fill="#A4D4E6" d="M 199.78 63.33 L 199.78 70.51 C 196.1 70.73 192.09 70.87 187.81 70.87 C 178.99 70.87 169.04 70.29 158.59 68.62 C 150.63 67.36 143.52 66.06 136.71 64.79 C 135.2 64.5 133.7 64.23 132.21 63.94 C 128.22 63.19 124.2 62.43 119.99 61.67 C 102.78 58.57 82.46 55.44 48.13 52.47 C 33.71 51.24 14.52 51.34 -0.03 51.71 L -0.03 44.57 C 6.54 44.4 14.03 44.29 21.64 44.34 C 31.14 44.39 40.82 44.69 49.15 45.4 C 92.47 49.13 113.79 53.15 134.39 57.03 C 135.02 57.14 135.65 57.27 136.29 57.38 C 141.84 58.43 147.58 59.49 153.76 60.54 C 155.92 60.92 158.15 61.29 160.45 61.66 C 175.22 64.0 189.09 64.01 199.78 63.33"/>
                            </g>
                            <g className="wave-line wave-line-4">
                                <path fill="#1F4F59" d="M 190.65 48.59 C 183.82 49.1 176.34 49.77 168.61 50.61 C 159.24 51.64 148.15 54.29 136.29 57.38 C 133.4 58.13 130.47 58.91 127.52 59.69 C 125.04 60.35 122.53 61.02 119.99 61.68 C 97.94 67.46 73.86 73.2 52.43 72.55 C 31.03 71.91 12.27 64.76 -0.03 58.64 L -0.03 67.07 C 13.07 73.03 31.26 79.05 52.08 79.67 C 53.3 79.7 54.53 79.72 55.78 79.72 C 80.38 79.72 106.95 72.67 130.52 66.43 C 132.61 65.87 134.67 65.32 136.71 64.79 C 142.64 63.23 148.37 61.76 153.76 60.54 C 159.63 59.22 165.08 58.19 169.9 57.66 C 180.74 56.48 191.09 55.65 199.78 55.08 L 199.78 47.94 C 196.91 48.12 193.86 48.33 190.65 48.59"/>
                            </g>
                        </svg>
                        <div className="loading-progress-bar-track">
                            <div className="loading-progress-bar-fill" style={{ width: `${loadingProgress}%` }}></div>
                        </div>
                        <p className="loading-label">
                            {loadingProgress < 30 ? 'Connecting...' : loadingProgress < 70 ? 'Fetching records...' : loadingProgress < 100 ? 'Almost there...' : 'Done'}
                        </p>
                    </div>
                </div>
            )}
            {toast.show && (
                <div className={`toast toast-${toast.type}`} role="alert" aria-live="polite">
                    <div className="toast-content">
                        {toast.type === 'success' && <BsCheckCircle className="toast-icon" />}
                        {toast.type === 'error' && <BsXCircle className="toast-icon" />}
                        {toast.type === 'info' && <BsInfoCircle className="toast-icon" />}
                        <span>{toast.message}</span>
                    </div>
                </div>
            )}
            <div className="page">
                <div className="actionBar">
                    <div className="actionBarImage">
                        <img alt="Carolina OMF Imaging" style={{height: 'clamp(36px, 5vh, 60px)', width: 'auto', padding: '8px 0'}} src={logo} />
                    </div>
                    <div className="accountContainer searchAccountContainer">
                        <div className={`searchBox${searchTerm ? ' is-active' : ''}`}>
                            <BsSearch className="searchBoxIcon" size={15} />
                            <input
                                id="searchTerm"
                                type="text"
                                placeholder="Search patients..."
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); }}
                                aria-label="Search patients by name"
                                autoComplete="off"/>
                            {searchTerm && (
                                <span className="searchResultsBadge">
                                    {filteredData.length}
                                </span>
                            )}
                            {searchTerm && (
                                <button
                                    className="clearButton"
                                    onClick={() => setSearchTerm('')}
                                    aria-label="Clear search"
                                    type="button"
                                >
                                    <BsX size={18} />
                                </button>
                            )}
                            <span className="searchBarProgress" aria-hidden="true"></span>
                        </div>
                    </div>
                    <div className="accountContainer">
                        <button className="accountItems" onClick={handleAccount}>Account</button>
                        {account ? (
                            <>
                                <button className="accountItems" onClick={onChangeColorMode} onChange={({ target }) => setColorMode(target.checked)}>
                                    {colorMode ? 
                                        <BsFillSunFill className="accountIconSun" size={22}/>
                                        : 
                                        <BsFillMoonFill className="accountIconMoon" size={22}/>
                                    }
                                </button>
                                <button className="accountItems" type="button" onClick={signOutHandle}>Logout</button>
                            </>
                        ) : null}
                    </div>
                </div>
                <div className="main-layout">
                <div className="containerManagement">
                    <div className="sectionsWrapper">
                        <div className="notReviewedSection">
                        <label htmlFor="formFile" id="mainLabel">Incomplete ({notReviewedCounter.length})</label>
                            <div className="itemRowTitleContainer">
                                <div id="itemColumn">ID</div>
                                <button id="itemColumnDate" onClick={() => { toggleDateSort(); } }>
                                    Date
                                    {sortDateToggle ? 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                        : 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                    }
                                </button>
                                <button id="itemColumnPatientName" onClick={() => { togglePatientNameSort(); } }>
                                    Patient Name
                                    {sortPatientNameToggle ? 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                        : 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                    }
                                </button>
                                <button id="itemColumn" onClick={() => { togglePractitionerSort(); } }>
                                    Practitioner
                                    <BsArrowDownUp className="dateSortIcon" size={22}/>
                                </button>
                                <div id="itemColumnContactEmail">Contact Email</div>
                                <div id="itemColumnReportPlus">Report Plus</div> 
                                <div id="itemColumn">Payment Method</div> 
                                <div id="itemControlsLabels"></div>
                            </div>
                            {filteredData.length > 0 ? 
                                <div className="rowHold">
                                    {filteredData.map((item, index) => {
                                        if (item.reviewed === false) {
                                            return (
                                            <div key={index} data-row-id={item.id} className={`row-card${showUpload && item.id === recordId ? ' row-expanded' : ''}${transitioningId === item.id ? ' row-card-transitioning' : ''}`}>
                                                <div key={item.id}>
                                                    <div className="itemRowContainer" role="button" tabIndex={0} onClick={() => openRowActions(item)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRowActions(item); } }}>
                                                        <div id="itemRow" data-label="ID">{item.id}</div>
                                                        <div id="itemRowDate" data-label="Date">{item.dttm}</div>
                                                        <div id="itemRow" data-label="Patient">{item.patient_name}</div>
                                                        <div id="itemRow" data-label="Practitioner">{item.practitioner_name}</div>
                                                        <div id="itemRowContactEmail" data-label="Email">{item.contact_email}</div>
                                                        <div id="itemRowContactReportPlus" data-label="Report+">{item.report_plus}</div>
                                                        <div id="itemRow" data-label="Payment">{item.payment_method}</div>
                                                        <div id="itemControlsButtonsUpload">
                                                            <button
                                                                className="actionsButton"
                                                                type="button"
                                                                tabIndex={-1}
                                                                aria-hidden="true"
                                                            >
                                                                <BsGear className="actionsButtonIcon" size={16} />
                                                                {(showUpload && item.id === recordId) ?
                                                                    <BsCaretUpFill size={12} /> :
                                                                    <BsCaretDownFill size={12} />
                                                                }
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {(showUpload && item.id === recordId) && (
                                                    <div className="uploadModal">
                                                        {/* Upload left panel */}
                                                        <div className="upload-left-panel">
                                                            {item?.finalDoc ? (
                                                                <div className="pdf-exists-card">
                                                                    <BsFiletypeDoc size={18} className="pdf-exists-icon" />
                                                                    <div className="pdf-exists-info">
                                                                        <span className="pdf-exists-title">Final PDF</span>
                                                                        <span className="pdf-exists-subtitle">Ready to download</span>
                                                                    </div>
                                                                    <div className="pdf-exists-actions">
                                                                        <button className="uploadItems" onClick={() => itemDownload(item.id, item?.finalDoc.finalDoc, item.patient_name, 'FinalPDF')}>
                                                                            Download
                                                                        </button>
                                                                        <button className="uploadItems upload-delete" onClick={() => itemDeletePDF(item.id)}>
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="upload-drop-zone-wrapper">
                                                                    {(pendingFile || isUploading) ? (
                                                                        <>
                                                                            <div className="pdf-pending-card">
                                                                                {isUploading ? <div className="upload-spinner" /> : <BsFiletypeDoc size={16} />}
                                                                                <span className="pdf-pending-name">{isUploading ? 'Uploading...' : pendingFile?.name}</span>
                                                                                {isUploading ? (
                                                                                    <span className="pdf-pending-size">{progress}%</span>
                                                                                ) : (
                                                                                    <span className="pdf-pending-size">{pendingFile ? `${(pendingFile.size / 1024).toFixed(0)} KB` : ''}</span>
                                                                                )}
                                                                            </div>
                                                                            {!isUploading && (
                                                                                <>
                                                                                    <button className="upload-cancel-btn" onClick={cancelFileSelect}>
                                                                                        Cancel
                                                                                    </button>
                                                                                    <button className="uploadItems" onClick={submitUpload}>
                                                                                        Submit
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <label className="upload-drop-zone" htmlFor="file_picker">
                                                                                <div className="upload-drop-icon"><BsCloudUpload size={20} /></div>
                                                                                <span className="upload-drop-title">Upload your PDF</span>
                                                                                <span className="upload-drop-hint">Click to browse · PDF files only</span>
                                                                            </label>
                                                                            <input
                                                                                id="file_picker"
                                                                                name="file"
                                                                                type="file"
                                                                                accept=".pdf"
                                                                                onChange={handleFileSelect}
                                                                                aria-label="Upload PDF file"
                                                                                className="upload-hidden-input"
                                                                            />
                                                                            <button className="upload-cancel-btn" onClick={() => toggleUpload(item.id)}>
                                                                                Cancel
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Divider */}
                                                        <div className="upload-panel-divider" />
                                                        {/* Action controls panel */}
                                                        <div className="upload-actions-panel">
                                                            {renderCompleteControl(item)}
                                                            <button className="action-control-item" disabled={downloadingId === item.id} onClick={() => itemDownload(item.id, item.submittedDoc?.submittedDoc, item.patient_name, 'Scans', undefined, item.practitioner_name)}>
                                                                {downloadingId === item.id ? <span className="spinner-icon" /> : <BsFileZip size={15} />}
                                                                <span>Scans</span>
                                                            </button>
                                                            <div className="action-control-dropdown-wrapper action-control-item">
                                                                <button className="action-control-dropdown-btn" onClick={() => { const opening = generatedDropdownOpen !== item.id + '-reviewed'; setGeneratedDropdownOpen(opening ? item.id + '-reviewed' : null); if (opening) { setTimeout(() => { const row = document.querySelector(`[data-row-id="${item.id}"]`); if (row) row.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); } }}>
                                                                    <BsFiletypeDoc size={15} />
                                                                    <span>Generated</span>
                                                                </button>
                                                                {generatedDropdownOpen === item.id + '-reviewed' && (
                                                                    <div className="action-control-dropdown">
                                                                        <button className="action-dropdown-item action-dropdown-customize" onClick={() => { setGeneratedDropdownOpen(null); navigate('/customize-template', { state: { patient: item } }); }}>
                                                                            <BsGear size={13} style={{marginRight: '4px'}} />
                                                                            Customize Template
                                                                        </button>
                                                                        <div className="action-dropdown-divider"></div>
                                                                        {getGeneratedDoc(item, 'TMJ') && (
                                                                            <button className="action-dropdown-item" onClick={() => { itemDownload(item.id, getGeneratedDoc(item, 'TMJ').generatedDoc, item.patient_name, 'Report', 'tmj'); setGeneratedDropdownOpen(null); }}>
                                                                                TMJ Template
                                                                            </button>
                                                                        )}
                                                                        {getGeneratedDoc(item, 'ENDO') && (
                                                                            <button className="action-dropdown-item" onClick={() => { itemDownload(item.id, getGeneratedDoc(item, 'ENDO').generatedDoc, item.patient_name, 'Report', 'endo'); setGeneratedDropdownOpen(null); }}>
                                                                                Endo Template
                                                                            </button>
                                                                        )}
                                                                        {getGeneratedDoc(item, 'GEN') && (
                                                                            <button className="action-dropdown-item" onClick={() => { itemDownload(item.id, getGeneratedDoc(item, 'GEN').generatedDoc, item.patient_name, 'Report', 'gen'); setGeneratedDropdownOpen(null); }}>
                                                                                General Template
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button className="action-control-item action-delete-item" onClick={() => itemDelete(item.id)}>
                                                                <BsCloudMinus size={15} />
                                                                <span>Delete Record</span>
                                                            </button>
                                                        </div>

                                                    </div>
                                                )}
                                            </div>
                                            )
                                        }
                                    })}
                                </div>
                            :
                                <div className="rowHold">
                                    {isLoading ? (
                                        <>
                                            {[1, 2, 3].map((i) => (
                                                <div key={i} className="skeleton-row">
                                                    <div className="skeleton-cell"></div>
                                                    <div className="skeleton-cell"></div>
                                                    <div className="skeleton-cell"></div>
                                                    <div className="skeleton-cell"></div>
                                                    <div className="skeleton-cell"></div>
                                                    <div className="skeleton-cell"></div>
                                                    <div className="skeleton-cell"></div>
                                                </div>
                                            ))}
                                        </>
                                    ) : (
                                        <div className="empty-state">
                                            <p className="empty-state-text">No patient referrals found</p>
                                            <p className="empty-state-subtext">
                                                {searchTerm ? "Try adjusting your search" : "New referrals will appear here"}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            }
                        {/* </div> */}
                        </div>
                        <div className="reviewedSection" style={{ flex: isReviewedMinimized ? '0 0 auto' : '1' }}>
                        <label htmlFor="formFile" id="mainLabel">
                            Complete ({reviewedCounter.length})
                            <button
                                onClick={() => setIsReviewedMinimized(!isReviewedMinimized)}
                                style={{
                                    border: 'none',
                                    background: 'transparent',
                                    cursor: 'pointer',
                                    padding: '5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginLeft: 'auto',
                                    color: 'inherit'
                                }}
                                aria-label={isReviewedMinimized ? "Expand reviewed section" : "Minimize reviewed section"}
                            >
                                {isReviewedMinimized ? <BsCaretDownFill size={20} /> : <BsCaretUpFill size={20} />}
                            </button>
                        </label>
                        <div className="reviewContiner" style={{ display: isReviewedMinimized ? 'none' : 'flex' }}>
                            <div className="itemRowTitleContainer">
                                <div id="itemColumn">ID</div>
                                <button id="itemColumnDate" onClick={() => { toggleDateSort(); } }>
                                    Date
                                    {sortDateToggle ? 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                        : 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                    }
                                </button>
                                <button id="itemColumnPatientName" onClick={() => { togglePatientNameSort(); } }>
                                    Patient Name
                                    {sortPatientNameToggle ? 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                        : 
                                        <BsArrowDownUp className="dateSortIcon" size={22}/>
                                    }
                                </button>
                                <button id="itemColumn" onClick={() => { togglePractitionerSort(); } }>
                                    Practitioner
                                    <BsArrowDownUp className="dateSortIcon" size={22}/>
                                </button>
                                <div id="itemColumnContactEmail">Contact Email</div>
                                <div id="itemColumnReportPlus">Report Plus</div> 
                                <div id="itemColumn">Payment Method</div> 
                                {/* <div id="itemControlsLabels">Reviewed</div>
                                <div id="itemControlsLabels">Upload</div> 
                                <div id="itemControlsLabels">Generate</div>  */}
                                <div id="itemControlsLabels"></div>
                                {/* <div id="itemControlsLabels">Delete</div>  */}
                            </div>
                            {filteredData.length > 0 ?
                                <div className="rowHold">
                                    {filteredData.map((item, index) => {
                                        if (item.reviewed === true) {
                                            return (
                                            <div key={index} data-row-id={item.id} className={`row-card${showUpload && item.id === recordId ? ' row-expanded' : ''}${transitioningId === item.id ? ' row-card-transitioning' : ''}`}>
                                                <div key={item.id}>
                                                    <div className="itemRowContainer" role="button" tabIndex={0} onClick={() => openRowActions(item)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openRowActions(item); } }}>
                                                        <div id="itemRow" data-label="ID">{item.id}</div>
                                                        <div id="itemRowDate" data-label="Date">{item.dttm}</div>
                                                        <div id="itemRow" data-label="Patient">{item.patient_name}</div>
                                                        <div id="itemRow" data-label="Practitioner">{item.practitioner_name}</div>
                                                        <div id="itemRowContactEmail" data-label="Email">{item.contact_email}</div>
                                                        <div id="itemRowContactReportPlus" data-label="Report+">{item.report_plus}</div>
                                                        <div id="itemRow" data-label="Payment">{item.payment_method}</div>
                                                        <div id="itemControlsButtonsUpload">
                                                            <button
                                                                className="actionsButton"
                                                                type="button"
                                                                tabIndex={-1}
                                                                aria-hidden="true"
                                                            >
                                                                <BsGear className="actionsButtonIcon" size={16} />
                                                                {(showUpload && item.id === recordId) ?
                                                                    <BsCaretUpFill size={12} /> :
                                                                    <BsCaretDownFill size={12} />
                                                                }
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {(showUpload && item.id === recordId) && (
                                                    <div className="uploadModal">
                                                        {/* Upload left panel */}
                                                        <div className="upload-left-panel">
                                                            {item?.finalDoc ? (
                                                                <div className="pdf-exists-card">
                                                                    <BsFiletypeDoc size={18} className="pdf-exists-icon" />
                                                                    <div className="pdf-exists-info">
                                                                        <span className="pdf-exists-title">Final PDF</span>
                                                                        <span className="pdf-exists-subtitle">Ready to download</span>
                                                                    </div>
                                                                    <div className="pdf-exists-actions">
                                                                        <button className="uploadItems" onClick={() => itemDownload(item.id, item?.finalDoc.finalDoc, item.patient_name, 'FinalPDF')}>
                                                                            Download
                                                                        </button>
                                                                        <button className="uploadItems upload-delete" onClick={() => itemDeletePDF(item.id)}>
                                                                            Delete
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="upload-drop-zone-wrapper">
                                                                    {(pendingFile || isUploading) ? (
                                                                        <>
                                                                            <div className="pdf-pending-card">
                                                                                {isUploading ? <div className="upload-spinner" /> : <BsFiletypeDoc size={16} />}
                                                                                <span className="pdf-pending-name">{isUploading ? 'Uploading...' : pendingFile?.name}</span>
                                                                                {isUploading ? (
                                                                                    <span className="pdf-pending-size">{progress}%</span>
                                                                                ) : (
                                                                                    <span className="pdf-pending-size">{pendingFile ? `${(pendingFile.size / 1024).toFixed(0)} KB` : ''}</span>
                                                                                )}
                                                                            </div>
                                                                            {!isUploading && (
                                                                                <>
                                                                                    <button className="upload-cancel-btn" onClick={cancelFileSelect}>
                                                                                        Cancel
                                                                                    </button>
                                                                                    <button className="uploadItems" onClick={submitUpload}>
                                                                                        Submit
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <label className="upload-drop-zone" htmlFor="file_picker">
                                                                                <div className="upload-drop-icon"><BsCloudUpload size={20} /></div>
                                                                                <span className="upload-drop-title">Upload your PDF</span>
                                                                                <span className="upload-drop-hint">Click to browse · PDF files only</span>
                                                                            </label>
                                                                            <input
                                                                                id="file_picker"
                                                                                name="file"
                                                                                type="file"
                                                                                accept=".pdf"
                                                                                onChange={handleFileSelect}
                                                                                aria-label="Upload PDF file"
                                                                                className="upload-hidden-input"
                                                                            />
                                                                            <button className="upload-cancel-btn" onClick={() => toggleUpload(item.id)}>
                                                                                Cancel
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* Divider */}
                                                        <div className="upload-panel-divider" />
                                                        {/* Action controls panel */}
                                                        <div className="upload-actions-panel">
                                                            {renderCompleteControl(item)}
                                                            <button className="action-control-item" disabled={downloadingId === item.id} onClick={() => itemDownload(item.id, item.submittedDoc?.submittedDoc, item.patient_name, 'Scans', undefined, item.practitioner_name)}>
                                                                {downloadingId === item.id ? <span className="spinner-icon" /> : <BsFileZip size={15} />}
                                                                <span>Scans</span>
                                                            </button>
                                                            <div className="action-control-dropdown-wrapper action-control-item">
                                                                <button className="action-control-dropdown-btn" onClick={() => { const opening = generatedDropdownOpen !== item.id; setGeneratedDropdownOpen(opening ? item.id : null); if (opening) { setTimeout(() => { const row = document.querySelector(`[data-row-id="${item.id}"]`); if (row) row.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100); } }}>
                                                                    <BsFiletypeDoc size={15} />
                                                                    <span>Generated</span>
                                                                    {/* <BsCaretDownFill size={10} /> */}
                                                                </button>
                                                                {generatedDropdownOpen === item.id && (
                                                                    <div className="action-control-dropdown">
                                                                        <button className="action-dropdown-item action-dropdown-customize" onClick={() => { setGeneratedDropdownOpen(null); navigate('/customize-template', { state: { patient: item } }); }}>
                                                                            <BsGear size={13} style={{marginRight: '4px'}} />
                                                                            Customize Template
                                                                        </button>
                                                                        <div className="action-dropdown-divider"></div>
                                                                        {getGeneratedDoc(item, 'TMJ') && (
                                                                            <button className="action-dropdown-item" onClick={() => { itemDownload(item.id, getGeneratedDoc(item, 'TMJ').generatedDoc, item.patient_name, 'Report', 'tmj'); setGeneratedDropdownOpen(null); }}>
                                                                                TMJ Template
                                                                            </button>
                                                                        )}
                                                                        {getGeneratedDoc(item, 'ENDO') && (
                                                                            <button className="action-dropdown-item" onClick={() => { itemDownload(item.id, getGeneratedDoc(item, 'ENDO').generatedDoc, item.patient_name, 'Report', 'endo'); setGeneratedDropdownOpen(null); }}>
                                                                                Endo Template
                                                                            </button>
                                                                        )}
                                                                        {getGeneratedDoc(item, 'GEN') && (
                                                                            <button className="action-dropdown-item" onClick={() => { itemDownload(item.id, getGeneratedDoc(item, 'GEN').generatedDoc, item.patient_name, 'Report', 'gen'); setGeneratedDropdownOpen(null); }}>
                                                                                General Template
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <button className="action-control-item action-delete-item" onClick={() => itemDelete(item.id)}>
                                                                <BsCloudMinus size={15} />
                                                                <span>Delete Record</span>
                                                            </button>
                                                        </div>

                                                    </div>
                                                )}
                                            </div>
                                            )
                                        }
                                    })}
                                </div>
                            :
                            <div className="rowHold">
                                {isLoading ? (
                                    <>
                                        {[1, 2, 3].map((i) => (
                                            <div key={i} className="skeleton-row">
                                                <div className="skeleton-cell"></div>
                                                <div className="skeleton-cell"></div>
                                                <div className="skeleton-cell"></div>
                                                <div className="skeleton-cell"></div>
                                                <div className="skeleton-cell"></div>
                                                <div className="skeleton-cell"></div>
                                                <div className="skeleton-cell"></div>
                                            </div>
                                        ))}
                                    </>
                                ) : (
                                    <div className="empty-state">
                                        <p className="empty-state-text">No reviewed referrals</p>
                                        <p className="empty-state-subtext">
                                            {searchTerm ? "Try adjusting your search" : "Reviewed referrals will appear here"}
                                        </p>
                                    </div>
                                )}
                            </div>
                            }
                        </div>
                        </div>
                    </div>
                </div>
                <div className={`kpi-sidebar${kpiCollapsed ? ' kpi-sidebar-collapsed' : ''}`}>
                    <button className="kpi-toggle" onClick={() => setKpiCollapsed(!kpiCollapsed)}>
                        <span className="kpi-toggle-label">Stats</span>
                        {kpiCollapsed ? <BsCaretDownFill size={12} /> : <BsCaretUpFill size={12} />}
                    </button>
                    {!kpiCollapsed && (
                        <div className="kpi-grid">
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.total}</span>
                                <span className="kpi-label">Total Submissions</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.monthlyCount}</span>
                                <span className="kpi-label">This Month</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.lastWeek}</span>
                                <span className="kpi-label">Last 7 Days</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.reviewedPercent}%</span>
                                <span className="kpi-label">Complete</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.topDoctor ? kpiData.topDoctor[0] : '—'}</span>
                                <span className="kpi-label">Top Practitioner ({kpiData.topDoctor ? kpiData.topDoctor[1] : 0})</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.newestDoctor || '—'}</span>
                                <span className="kpi-label">Newest Doctor</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.uniqueOffices}</span>
                                <span className="kpi-label">Unique Offices</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.avgPerMonth}</span>
                                <span className="kpi-label">Avg / Month</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.topPayment ? kpiData.topPayment[0] : '—'}</span>
                                <span className="kpi-label">Top Payment ({kpiData.topPayment ? kpiData.topPayment[1] : 0})</span>
                            </div>
                            <div className="kpi-card">
                                <span className="kpi-value">{kpiData.avgReviewLabel}</span>
                                <span className="kpi-label">Avg Review Time</span>
                            </div>
                        </div>
                    )}
                </div>
                </div>
            </div>
        </div>
    )
}

export default Management;
